// src/lib/corgeeHelper.ts
// Utility functions for Corgee application
// Modernized version removing jQuery dependencies

import moment from 'moment';
import { authStorage, sessionStorageUtil } from './storage';

/**
 * Detect if browser is Safari
 */
export function isSafari(): boolean
{
  const userAgent = navigator.userAgent || navigator.vendor;
  return (
    userAgent.indexOf('Safari') !== -1 &&
    userAgent.indexOf('Chrome') === -1 &&
    userAgent.indexOf('CrMo') === -1 &&
    userAgent.indexOf('CriOS') === -1
  );
}

/**
 * Detect if browser is Firefox
 */
export function isFirefox(): boolean
{
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.indexOf('firefox') > -1;
}

/**
 * Check if content should show "see more" option
 */
export function showSeeMoreContent(body: string | null, createdBy?: string): boolean
{
  if (!body)
  {
    return false;
  }

  if (body.indexOf('<div class="detail-update-embed') === 0 && createdBy === 'admin')
  {
    return false;
  }

  return true;
}

/**
 * Get image file extensions
 */
export function getImageExtensions(): string[]
{
  return ['.jpg', '.jpe', '.bmp', '.gif', '.png', '.jpeg', '.webp'];
}

/**
 * Check if extension is an image extension
 */
export function extInImageExtensions(extension: string | undefined | null): boolean
{
  if (!extension) return false;
  const imageExtensions = getImageExtensions();
  return imageExtensions.includes(extension.toLowerCase());
}

/**
 * Get document icon class name by file extension
 */
export function getDocumentClassName(docType: string | undefined | null): string
{
  if (!docType) return '';
  const lowerType = docType.toLowerCase();

  switch (lowerType)
  {
    case '.pdf':
      return 'Pdf';
    case '.doc':
    case '.docx':
      return 'Doc';
    case '.xls':
    case '.xlsx':
      return 'Xls';
    case '.zip':
      return 'Zip';
    case '.rar':
      return 'Rar';
    case '.ppt':
    case '.pptx':
      return 'Ppt';
    case '.psd':
    case '.psb':
      return 'Psd';
    case '.ai':
    case '.svg':
      return 'Ai';
    case '.pur':
      return 'Pur';
    case '.3gp':
    case '.mp4':
    case '.mov':
    case '.ogg':
    case '.webm':
    case '.f4v':
    case '.m4v':
    case '.mkv':
      return 'Vid';
    default:
      return 'Def';
  }
}

/**
 * Check if item exists in array
 */
export function inArray<T>(arr: T[] | null | undefined, itemValue: T): boolean
{
  if (!arr || arr.length === 0)
  {
    return false;
  }

  return arr.includes(itemValue);
}

/**
 * Permission checks
 */

interface ConversationItem {
  ConfigValueId?: number;
  OwnedById?: number;
  CreatedById?: number;
  CreatedByID?: number;
  IsEnable?: boolean;
  Body?: string | null;
  Attachments?: any[];
  CreatedAt?: string;
}

/**
 * Check if user can edit conversation detail history
 */
export function canEditDetailHistory(conversation: ConversationItem): boolean
{
  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  return data.canEdit || conversation.OwnedById === personIdNum;
}

/**
 * Check if user has edit permissions for conversation
 */
export function convHasEditPermissions(
  conversation: ConversationItem | null,
  reply?: ConversationItem,
): boolean
{
  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  if (conversation)
  {
    // Check if conversation is disabled
    if (conversation.ConfigValueId === 15 || conversation.IsEnable === false)
    {
      return false;
    }

    // Admin always has edit permission
    if (data.isAdmin)
    {
      return true;
    }

    // Special case for ConfigValueId 9
    if (conversation.ConfigValueId === 9 && conversation.OwnedById === personIdNum)
    {
      return data.canEdit;
    }

    // Regular edit permission
    if (data.canEdit)
    {
      return true;
    }
  }
  else if (reply)
  {
    // Admin always has edit permission
    if (data.isAdmin)
    {
      return true;
    }

    // Check reply creator
    const replyCreatorId = reply.CreatedById || reply.CreatedByID;
    if (data.canEdit && replyCreatorId === personIdNum)
    {
      return true;
    }
  }

  return false;
}

/**
 * Check if user can edit the title of a conversation (mirrors legacy validateEditPermissions).
 * More restrictive than convHasEditPermissions: requires the user to be the creator or owner,
 * or the card to be a type-9 board. Type-15 cards are always blocked.
 */
export function convHasTitleEditPermissions(conversation: ConversationItem): boolean
{
  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  if (conversation.ConfigValueId === 15 || conversation.IsEnable === false) return false;
  if (data.isAdmin) return true;

  const createdById = conversation.CreatedByID ?? conversation.CreatedById ?? 0;
  if (
    data.canEdit &&
    (createdById === personIdNum ||
      conversation.OwnedById === personIdNum ||
      conversation.ConfigValueId === 9)
  )
  {
    return true;
  }

  return false;
}

/**
 * Check if user has delete permissions for conversation
 */
export function convHasDeletePermissions(conversation: ConversationItem): boolean
{
  if (!conversation || conversation.ConfigValueId === 15)
  {
    return false;
  }

  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  // Admin always has delete permission
  if (data.isAdmin)
  {
    return true;
  }

  // Owner with delete permission
  if (data.canDelete && conversation.OwnedById === personIdNum)
  {
    return true;
  }

  return false;
}

/**
 * Check if user has edit permissions for reply
 */
export function replyHasEditPermissions(reply: ConversationItem): boolean
{
  if (!reply)
  {
    return false;
  }

  // Check if reply is disabled
  if (reply.IsEnable === false)
  {
    return false;
  }

  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  // Admin always has edit permission
  if (data.isAdmin)
  {
    return true;
  }

  // Creator with edit permission
  const replyCreatorId = reply.CreatedByID || reply.CreatedById;
  if (data.canEdit && replyCreatorId === personIdNum)
  {
    return true;
  }

  return false;
}

/**
 * Check if user has temporary edit permissions for reply (within 15 minutes)
 */
export function replyHasEditPermissionsTemporary(reply: ConversationItem): boolean
{
  if (!reply)
  {
    return false;
  }

  // Check if reply is disabled
  if (reply.IsEnable === false)
  {
    return false;
  }

  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  // Only for users with canTemporary permission (not admins or editors)
  if (!data.canEdit && !data.isAdmin && data.canTemporary && reply.CreatedById === personIdNum)
  {
    if (!reply.CreatedAt) return false;

    const dateNow = new Date();
    const dateReply = new Date(reply.CreatedAt);
    const diffMs = dateNow.getTime() - dateReply.getTime();
    const diffMins = Math.round(diffMs / 60000);

    // Allow edit within 15 minutes
    if (diffMins < 16)
    {
      return true;
    }
  }

  return false;
}

/**
 * Check if user has delete permissions for reply
 */
export function replyHasDeletePermissions(reply: ConversationItem): boolean
{
  if (!reply)
  {
    return false;
  }

  // Check if reply is disabled
  if (reply.IsEnable === false)
  {
    return false;
  }

  const personId = authStorage.getPersonId();
  if (!personId) return false;

  const personIdNum = parseInt(personId, 10);
  const data = sessionStorageUtil.getPersonData();
  if (!data) return false;

  // Admin always has delete permission
  if (data.isAdmin)
  {
    return true;
  }

  // Creator with delete permission
  const replyCreatorId = reply.CreatedByID || reply.CreatedById;
  if (data.canDelete && replyCreatorId === personIdNum)
  {
    return true;
  }

  return false;
}

/**
 * Format date using moment.js style
 */
export function getMomentDateTime(dateTimeValue: string | null | undefined): string
{
  if (!dateTimeValue)
  {
    return '';
  }

  const date = new Date(dateTimeValue);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60)
  {
    return 'just now';
  }
  else if (diffMins < 60)
  {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  }
  else if (diffHours < 24)
  {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  else if (diffDays < 7)
  {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  else
  {
    return date.toLocaleDateString();
  }
}

/**
 * Format date with specific format
 */
export function applyFormatDate(
  dateTimeValue: string | null | undefined,
  formatDate: string,
): string
{
  if (!dateTimeValue)
  {
    return '';
  }

  return moment(dateTimeValue).format(formatDate);
}

/**
 * Validate and get conversation/reply body HTML
 */
export function getBodyValidated(item: ConversationItem, isReply: boolean = false): string
{
  const defaultEmpty = '<p><br></p>';

  if (!item.Body)
  {
    // Check if has attachments or special config
    if ((item.Attachments && item.Attachments.length > 0) || item.ConfigValueId === 15)
    {
      return item.Body || defaultEmpty;
    }

    // Show empty state based on edit permissions
    const canEdit = isReply ? replyHasEditPermissions(item) : convHasEditPermissions(item);

    if (canEdit)
    {
      return '<div class="update-body-hover"><p title="Double click to update"></p></div>';
    }
    else
    {
      return '<div><p></p></div>';
    }
  }

  // Check if it's a temporary reply with moving dots
  const isTemporaryReply = item.Body.indexOf('appendMovingDots') !== -1;

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = item.Body;

  // Check if body has actual content
  const hasText = tempDiv.textContent?.trim().length ?? 0 > 0;
  const hasImages = tempDiv.querySelectorAll('img').length > 0;

  if (
    hasText ||
    hasImages ||
    isTemporaryReply ||
    (item.Attachments && item.Attachments.length > 0) ||
    item.ConfigValueId === 15
  )
  {
    return item.Body;
  }

  // Empty body - show based on permissions
  const canEdit = isReply ? replyHasEditPermissions(item) : convHasEditPermissions(item);

  if (canEdit)
  {
    return '<div class="update-body-hover"><p title="Double click to update"></p></div>';
  }
  else
  {
    return '<div><p></p></div>';
  }
}

/**
 * Returns the ConfigValueID for Bookmarks by reading C2_sideMenuItems from sessionStorage.
 * Falls back to 148 if the item is not found.
 */
export function getBookmarksConfigValueId(): number
{
  try
  {
    const items: Array<{ Name: string; ConfigValueID: number }> = JSON.parse(
      sessionStorage.getItem('C2_sideMenuItems') ?? '[]',
    );
    const bookmarksItem = items.find(item => item.Name === 'Bookmarks');
    return bookmarksItem?.ConfigValueID ?? 148;
  }
  catch
  {
    return 148;
  }
}

/**
 * Calculate dropdown menu position (Viewport Relative for fixed positioning)
 */
export function getDropdownMenuPosition(button: any): { top: number; left: number; dropdown: any }
{
  // Handle jQuery objects - extract native DOM element
  let nativeButton: HTMLElement | null = null;

  if (button && typeof button.get === 'function')
  {
    nativeButton = button.get(0);
  }
  else if (button && button[0] && button[0].nodeType)
  {
    nativeButton = button[0];
  }
  else if (button instanceof HTMLElement)
  {
    nativeButton = button;
  }
  else
  {
    nativeButton = button as HTMLElement;
  }

  if (!nativeButton || !nativeButton.getBoundingClientRect)
  {
    return { top: 0, left: 0, dropdown: null };
  }

  // Support both tabs and card menu classes
  let actionMenuContainer = nativeButton.closest('.corgee_com_card-action-menu-group-tabs');
  let isTabs = true;

  if (!actionMenuContainer)
  {
    actionMenuContainer = nativeButton.closest('.corgee_com_card-action-menu-group');
    isTabs = false;
  }

  if (!actionMenuContainer)
  {
    const rect = nativeButton.getBoundingClientRect();
    return {
      top: rect.top + rect.height, // Viewport relative
      left: rect.left,
      dropdown: null,
    };
  }

  // Find dropdown menu
  let dropdown: Element | null = null;
  if (isTabs)
  {
    dropdown = actionMenuContainer.querySelectorAll
      ? (actionMenuContainer.querySelectorAll('.corgee_com_card-action-header-menu-tabs')[0] ??
        null)
      : null;
    if (!dropdown)
    {
      dropdown = document.querySelectorAll('.corgee_com_card-action-header-menu-tabs')[0] ?? null;
    }
  }
  else
  {
    dropdown = document.querySelectorAll('.corgee_com_card-action-header-menu')[0] ?? null;
  }

  // If still no dropdown, try generic dropdown-menu
  if (!dropdown)
  {
    dropdown = document.querySelectorAll('.dropdown-menu')[0] ?? null;
  }

  let newTopPosition = 0;
  let newLeftPosition = 0;

  const windowH = window.innerHeight;
  const windowW = window.innerWidth;

  // Use getBoundingClientRect for accurate positioning
  const buttonRect = nativeButton.getBoundingClientRect();

  const dropdownW = (dropdown as HTMLElement)?.offsetWidth || 200;
  const dropdownH = (dropdown as HTMLElement)?.offsetHeight || 300;

  // Position relative to VIEWPORT (for position: fixed)
  if (buttonRect.top <= windowH / 2)
  {
    newTopPosition = buttonRect.top + buttonRect.height;
  }
  else
  {
    newTopPosition = buttonRect.top - dropdownH;
  }

  if (buttonRect.left > windowW - dropdownW)
  {
    newLeftPosition = buttonRect.left + buttonRect.width - dropdownW;
  }
  else
  {
    newLeftPosition = buttonRect.left;
  }

  return {
    top: newTopPosition,
    left: newLeftPosition,
    dropdown: dropdown,
  };
}
